import React from 'react';

import { BookOpen, Construction, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Text } from 'components/Text';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

import type { UnderConstructionProps, UnderConstructionVariant } from './types';

/**
 * Configuration for each variant
 */
const variantConfig: Record<
  UnderConstructionVariant,
  {
    icon: React.ElementType;
    iconSize: string;
    title: string;
    description: string;
    containerClass: string;
  }
> = {
  default: {
    icon: Wrench,
    iconSize: 'w-16 h-16',
    title: 'EM CONSTRUÇÃO...',
    description: 'Esta funcionalidade está em desenvolvimento e ficará disponível em breve.',
    containerClass: 'h-[75%] border-2 border-brand-gold/50 rounded-xl mt-16',
  },
  'ai-training': {
    icon: BookOpen,
    iconSize: 'w-20 h-20',
    title: 'IA em treinamento...',
    description: 'Esta funcionalidade está sendo treinada e ficará disponível em breve.',
    containerClass: 'h-[80%] border-2 border-brand-gold/50 rounded-xl mt-16',
  },
  fullscreen: {
    icon: Construction,
    iconSize: 'w-12 h-12 md:w-16 md:h-16',
    title: 'Página em construção!',
    description:
      'Ops! Esta área ainda está sendo construída. Enquanto isso, dê uma olhada na versão para computador.',
    containerClass: 'fixed inset-0 z-[100] bg-background',
  },
  compact: {
    icon: Wrench,
    iconSize: 'w-8 h-8',
    title: 'Em construção',
    description: '',
    containerClass: 'h-full min-h-[200px] border border-border rounded-lg',
  },
};

/**
 * Unified UnderConstruction Component
 *
 * @component
 * @description Displays a placeholder message for features currently under development.
 * Consolidates 4 previous implementations into a single component with variants.
 *
 * @example
 * ```tsx
 * // Default variant (generic construction)
 * <UnderConstruction />
 *
 * // AI training variant
 * <UnderConstruction variant="ai-training" />
 *
 * // Fullscreen variant (for mobile/tablet)
 * <UnderConstruction variant="fullscreen" />
 *
 * // With dashboard button
 * <UnderConstruction showDashboardButton />
 *
 * // Custom title and description
 * <UnderConstruction
 *   title="Custom Title"
 *   description="Custom description text"
 * />
 * ```
 *
 * @features
 * - **4 Variants**: default, ai-training, fullscreen, compact
 * - **Custom Text**: Override title and description
 * - **Dashboard Navigation**: Optional button to navigate to cockpit
 * - **Accessibility**: ARIA attributes for screen readers
 *
 * @accessibility
 * - role="status" for live region semantics
 * - aria-labelledby links to heading
 * - aria-live="polite" for screen reader announcements
 * - Screen reader-only explanation text
 * - Decorative icon marked with aria-hidden
 */
export const UnderConstruction: React.FC<UnderConstructionProps> = ({
  variant = 'default',
  title,
  description,
  showDashboardButton = false,
  className,
}) => {
  const navigate = useNavigate();
  const config = variantConfig[variant];
  const Icon = config.icon;

  const displayTitle = title ?? config.title;
  const displayDescription = description ?? config.description;

  const handleBackToDashboard = () => {
    navigate('/cockpit');
  };

  // Special layout for fullscreen variant
  if (variant === 'fullscreen') {
    return (
      <div className={cn(config.containerClass, className)}>
        <div
          className={cn(
            'flex h-4/5 flex-col items-center justify-center gap-4 px-[5%] text-center text-foreground',
            'md:items-start md:justify-end md:pb-[10%] md:pl-[20%] md:text-start'
          )}
          role="status"
          aria-live="polite"
          aria-labelledby="construction-heading"
        >
          <div className="mb-6 h-[60px] w-[60px] md:h-12 md:w-12">
            <Icon className="h-full w-full text-warning" aria-hidden="true" />
          </div>

          <h1
            id="construction-heading"
            className="font-poppins text-2xl font-normal text-foreground md:text-start"
          >
            {displayTitle}
          </h1>

          <div className="mt-8 flex flex-col items-center justify-center gap-4 md:items-start md:justify-start">
            <p className="max-w-[500px] font-roboto text-base font-normal leading-4 text-foreground md:text-start">
              {displayDescription}
            </p>
          </div>

          <span className="sr-only">
            {displayDescription || 'Esta funcionalidade está em desenvolvimento.'}
          </span>

          {showDashboardButton && (
            <Button
              variant="default"
              size="medium"
              onClick={handleBackToDashboard}
              className="mt-4"
            >
              Ir para o Dashboard Principal
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Standard layout for other variants
  return (
    <section
      role="status"
      aria-live="polite"
      aria-labelledby="construction-heading"
      className={cn(
        'flex flex-col items-center justify-center gap-4 p-6 w-full',
        config.containerClass,
        className
      )}
    >
      <Icon
        data-testid="construction-icon"
        className={cn(config.iconSize, 'text-foreground')}
        aria-hidden="true"
      />

      <h2 id="construction-heading">
        <Text size={variant === 'compact' ? 'small' : 'xSmall'} bold>
          {displayTitle}
        </Text>
      </h2>

      <p className="sr-only">
        {displayDescription || 'Esta funcionalidade está em desenvolvimento.'}
      </p>

      {showDashboardButton && (
        <Button variant="default" size="medium" onClick={handleBackToDashboard} className="mt-4">
          Ir para o Dashboard Principal
        </Button>
      )}
    </section>
  );
};

export default UnderConstruction;
export type { UnderConstructionProps, UnderConstructionVariant } from './types';
